import { useSetSearchParams } from '@hook/useSetSearchParams';
import { fetchData } from '@network/querys';
import classNames from 'classnames';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {appConfig} from "@common/config";

export interface ICategoryProp {
    watchD1: string;
    watchD2: string;
    watchD3?: string;
    title: string;
    path: string;
    ga360str: string;
    activeCategory1: string | null;
    activeCategory2: string | null;
    activeCategory3: string | null;
    submitCallback?: (d1: string, d2: string) => void;
}

export interface ICategoryData {
    id: string;
    name: string;
    child: Array<{
        id: string;
        name: string;
        child?: Array<{
            id: string;
            name: string;
        }>;
    }>;
}

export const Category = ({ props }: { props: ICategoryProp }) => {
    const { watchD1, watchD2, watchD3, title, path, submitCallback, ga360str, activeCategory1, activeCategory2, activeCategory3 } = props;
    const setParams = useSetSearchParams();
    const [dat, datSet] = useState<ICategoryData[]>([]);
    const [depth, depthSet] = useState<{ d1: string | null; d2: string | null; d3: string | null }>({
        d1: null,
        d2: null,
        d3: null
    });
    const { t } = useTranslation();

    useEffect(() => {
        const locale = appConfig.locale;
        fetchData(path, { locale }).then((res: ICategoryData[]) => datSet(res));
    }, [path]);

    useEffect(() => {
        // ID를 문자열로 처리
        const d1 = activeCategory1 ? String(activeCategory1) : null;
        const d2 = activeCategory2 ? String(activeCategory2) : null;
        const d3 = activeCategory3 ? String(activeCategory3) : null;
        depthSet({ d1, d2, d3 });
    }, [activeCategory1, activeCategory2, activeCategory3]);

    // ID 비교 함수
    const compareIds = (id1: string | null, id2: string | null): boolean => {
        if (!id1 || !id2) return false;
        return String(id1).toLowerCase() === String(id2).toLowerCase();
    };

    const onClickCategory = (e: React.MouseEvent<HTMLButtonElement>) => {
        const targ = e.currentTarget as HTMLElement;
        const d1 = targ.dataset.d1 || '';
        const d2 = targ.dataset.d2 || '';
        const d3 = targ.dataset.d3 || '';

        const params: Record<string, string> = {
            page: '',
            [watchD1]: d1,
            [watchD2]: d2,
        };

        if (typeof watchD3 === 'string') {
            params[watchD3] = d3;
        }

        setParams(params);
        submitCallback?.(d1, d2);
    };

    return (
        <section className="category">
            <header>{title}</header>
            <article>
                <button className="btn-category" data-d1="" data-d2="" data-d3="" onClick={onClickCategory}>
                    {t('common.all')}
                </button>
                <ul>
                    {dat.map((it, idx) => (
                        <li
                            data-dep={it.id}
                            key={'cate' + idx}
                            className={classNames({ 'is-active': compareIds(it.id, depth.d1) })}
                        >
                            <button
                                className="btn-category"
                                data-d1={it.id}
                                data-areas="시세_조건검색_카테고리_클릭"
                                data-contents={it.name}
                                onClick={onClickCategory}
                            >
                                {it.name}
                            </button>
                            <ul>
                                {it.child?.map((sub, subidx) => (
                                    <li
                                        data-dep={`${it.id}-${sub.id}`}
                                        key={'cate' + idx + subidx}
                                        className={classNames({
                                            'is-active': compareIds(it.id, depth.d1) && compareIds(sub.id, depth.d2)
                                        })}
                                    >
                                        <button
                                            className="btn-category"
                                            data-d1={it.id}
                                            data-d2={sub.id}
                                            data-areas={ga360str}
                                            data-contents={`${it.name}-${sub.name}`}
                                            data-has-child={!!sub.child}
                                            onClick={onClickCategory}
                                        >
                                            {sub.name}
                                        </button>
                                        {sub.child && (
                                            <ul>
                                                {sub.child.map((third, thirdIdx) => (
                                                    <li
                                                        data-dep={`${it.id}-${sub.id}-${third.id}`}
                                                        key={'cate' + idx + subidx + thirdIdx}
                                                        className={classNames({
                                                            'is-active':
                                                                compareIds(it.id, depth.d1) &&
                                                                compareIds(sub.id, depth.d2) &&
                                                                compareIds(third.id, depth.d3)
                                                        })}
                                                    >
                                                        <button
                                                            className="btn-category"
                                                            data-d1={it.id}
                                                            data-d2={sub.id}
                                                            data-d3={third.id}
                                                            data-areas={ga360str}
                                                            data-contents={`${it.name}-${sub.name}-${third.name}`}
                                                            onClick={onClickCategory}
                                                        >
                                                            {third.name}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </li>
                    ))}
                </ul>
            </article>
        </section>
    );

};
